/* VPS PCQ — theme.js
   Lightweight, no framework. Wires:
   - floating nav on scroll
   - sticky ATC visibility (after scrolling past hero buy box)
   - generic accordions ([data-accordion])
   - PDP timeline scroll-driven active state (IntersectionObserver)
   - FAQ accordions
   - pack selector + sub/one-time toggle + price recompute
   - Recharge selling-plan integration on add-to-cart
*/
(function () {
  'use strict';

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  const prefersReducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------- Modal/drawer focus trap + restoration ----------
  const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  const focusReturnMap = new WeakMap();
  const openDialog = (el) => {
    const previouslyFocused = document.activeElement;
    if (previouslyFocused && previouslyFocused !== document.body) {
      focusReturnMap.set(el, previouslyFocused);
    }
    const firstFocusable = el.querySelector(FOCUSABLE);
    if (firstFocusable) setTimeout(() => firstFocusable.focus(), 50);
  };
  const closeDialog = (el) => {
    const restore = focusReturnMap.get(el);
    if (restore && typeof restore.focus === 'function') {
      restore.focus();
      focusReturnMap.delete(el);
    }
  };
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const open = document.querySelector('[data-modal][data-open="true"], [data-drawer][data-open="true"]');
    if (!open) return;
    const focusables = Array.from(open.querySelectorAll(FOCUSABLE)).filter((n) => !n.hasAttribute('disabled') && n.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  // ---------- 0. Back-to-top links (smooth scroll) ----------
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-scroll-top]');
    if (!link) return;
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  // ---------- 1. Floating nav on scroll ----------
  const headerWrap = $('.header-wrap');
  if (headerWrap) {
    const updateScrolled = () => {
      headerWrap.dataset.scrolled = (window.scrollY > 24).toString();
    };
    updateScrolled();
    window.addEventListener('scroll', updateScrolled, { passive: true });
  }

  // ---------- 2. Mobile menu toggle ----------
  $$('.header__menu-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const nav = $('.header__nav');
      if (!nav) return;
      nav.classList.toggle('header__nav--open');
    });
  });

  // ---------- 3. Generic accordion ----------
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-acc-trigger]');
    if (!trigger) return;
    e.preventDefault();
    const item = trigger.closest('[data-acc-item]');
    if (!item) return;
    const open = item.dataset.open === 'true';
    item.dataset.open = (!open).toString();
    trigger.setAttribute('aria-expanded', (!open).toString());
  });
  // Initial sync of aria-expanded on accordions that load open by default
  $$('[data-acc-item]').forEach((item) => {
    const trigger = item.querySelector('[data-acc-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', (item.dataset.open === 'true').toString());
  });

  // ---------- 4. PDP buy-box state machine (per [data-buy-box] instance) ----------
  const buyBoxes = $$('[data-buy-box]');
  if (buyBoxes.length) {
    const productJson = (() => {
      const el = $('[data-product-json]');
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (_) { return null; }
    })();

    const formatMoney = (cents) => {
      if (typeof cents !== 'number' || isNaN(cents)) return '';
      return '$' + (cents / 100).toFixed(2);
    };

    const findVariant = (id) => {
      if (!productJson || !productJson.variants) return null;
      return productJson.variants.find((v) => String(v.id) === String(id));
    };

    const initBuyBox = (buyBox) => {
    const isMain = buyBox.dataset.instance !== 'sticky';
    const subFirstPct = parseInt(buyBox.dataset.subFirst || '20', 10);
    const subRecPct = parseInt(buyBox.dataset.subRecurring || '15', 10);
    const singleBottleC = parseInt(buyBox.dataset.singleBottleCents || '3987', 10);

    const packCards = $$('.pack-card', buyBox);

    // Default active pack: most-popular if present, else first
    let initialPackId = packCards[0] && packCards[0].dataset.packId;
    const popular = packCards.find((c) => c.dataset.mostPopular === 'true');
    if (popular) initialPackId = popular.dataset.packId;

    const state = {
      packId: initialPackId,
      variantId: buyBox.dataset.initialVariantId || (productJson && productJson.variants && productJson.variants[0] && productJson.variants[0].id),
      mode: 'subscribe', // 'subscribe' | 'onetime'
      sellingPlanId: null,
      frequency: '30',
    };

    const getActiveCard = () => packCards.find((c) => c.dataset.packId === state.packId) || packCards[0];

    const updatePackUi = () => {
      packCards.forEach((card) => {
        const isActive = card.dataset.packId === state.packId;
        card.dataset.active = isActive.toString();
        card.setAttribute('aria-checked', isActive.toString());
        card.setAttribute('tabindex', isActive ? '0' : '-1');
      });
      // Sync selected variant id from active card
      const active = getActiveCard();
      if (active && active.dataset.variantId) state.variantId = active.dataset.variantId;
    };

    const updateModeUi = () => {
      $$('.purchase-toggle__option', buyBox).forEach((opt) => {
        const isActive = opt.dataset.mode === state.mode;
        opt.dataset.active = isActive.toString();
        opt.setAttribute('aria-checked', isActive.toString());
        opt.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    };

    const updateFrequencyUi = () => {
      $$('.purchase-toggle__freq-pill', buyBox).forEach((pill) => {
        const isActive = pill.dataset.frequency === state.frequency;
        pill.dataset.active = isActive.toString();
        pill.setAttribute('aria-checked', isActive.toString());
        pill.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    };

    // Roving-tabindex arrow-key navigation for any [role="radiogroup"] inside the buy box
    const wireRadiogroupKeys = (group) => {
      group.addEventListener('keydown', (e) => {
        const radios = $$('[role="radio"]', group);
        if (!radios.length) return;
        const currentIdx = radios.indexOf(document.activeElement);
        if (currentIdx === -1) return;
        let nextIdx = currentIdx;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') nextIdx = (currentIdx + 1) % radios.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') nextIdx = (currentIdx - 1 + radios.length) % radios.length;
        else if (e.key === 'Home') nextIdx = 0;
        else if (e.key === 'End') nextIdx = radios.length - 1;
        else return;
        e.preventDefault();
        radios[nextIdx].focus();
        radios[nextIdx].click();
      });
    };
    $$('[role="radiogroup"]', buyBox).forEach(wireRadiogroupKeys);

    const recomputePricing = () => {
      // Update each pack card price + per-day (independent of active state)
      packCards.forEach((card) => {
        const oneTimeC = parseInt(card.dataset.price || '0', 10);
        const bottles = parseInt(card.dataset.bottles || '1', 10);
        const originalC = singleBottleC * bottles;
        const firstSubC = Math.round(oneTimeC * (1 - subFirstPct / 100));
        const recSubC = Math.round(oneTimeC * (1 - subRecPct / 100));
        const nowEl = $('.pack-card__price-now', card);
        const compareEl = $('.pack-card__price-compare', card);
        const saveEl = $('[data-pack-save]', card);
        const perDayEl = $('.pack-card__per-day', card);
        const currentC = state.mode === 'subscribe' ? firstSubC : oneTimeC;
        if (nowEl) nowEl.textContent = formatMoney(currentC);
        // Subscribe: anchor the compare/savings to the pack's own regular price (shows the real subscription discount).
        // One-time: show the price only, no compare/savings.
        if (state.mode === 'subscribe' && oneTimeC > currentC) {
          if (compareEl) compareEl.textContent = formatMoney(oneTimeC);
          if (saveEl) {
            const pctOff = Math.round(((oneTimeC - currentC) / oneTimeC) * 100);
            saveEl.textContent = 'Save ' + pctOff + '%';
            saveEl.dataset.visible = 'true';
          }
        } else {
          if (compareEl) compareEl.textContent = '';
          if (saveEl) {
            saveEl.textContent = '';
            saveEl.dataset.visible = 'false';
          }
        }
        const servings = parseInt(card.dataset.servings || '0', 10);
        if (perDayEl && servings > 0 && oneTimeC > 0) {
          const perDayC = state.mode === 'subscribe' ? recSubC / servings : oneTimeC / servings;
          perDayEl.textContent = '~$' + (perDayC / 100).toFixed(2) + '/day';
        } else if (perDayEl) {
          perDayEl.textContent = '';
        }
      });

      // Active pack drives the headline + sticky atc price + toggle save text
      const active = getActiveCard();
      const oneTimeC = active ? parseInt(active.dataset.price || '0', 10) : 0;
      const activeBottles = active ? parseInt(active.dataset.bottles || '1', 10) : 1;
      const activeOriginalC = singleBottleC * activeBottles;
      const firstSubC = Math.round(oneTimeC * (1 - subFirstPct / 100));
      const recSubCActive = Math.round(oneTimeC * (1 - subRecPct / 100));
      const firstPctVsOriginal = oneTimeC > 0 ? Math.round(((oneTimeC - firstSubC) / oneTimeC) * 100) : 0;
      const recPctVsOriginal = oneTimeC > 0 ? Math.round(((oneTimeC - recSubCActive) / oneTimeC) * 100) : 0;

      // (Prices removed from purchase-toggle; live only in pack cards + ATC)
      // Subscribe subcopy: full per-pack percent breakdown when subscribe is active,
      // short non-numeric nudge when one-time is active.
      const subSubEl = $('[data-mode="subscribe"] .purchase-toggle__sub', buyBox);
      if (subSubEl) {
        if (state.mode === 'subscribe') {
          subSubEl.innerHTML = 'Get <strong data-sub-first-pct>' + firstPctVsOriginal + '</strong>% off your first order, then <strong data-sub-rec-pct>' + recPctVsOriginal + '</strong>% off every refill. Pause or cancel anytime.';
        } else {
          subSubEl.textContent = 'Save more on every order. Pause or cancel anytime.';
        }
      }

      const atcRecurEl = $('[data-atc-recurring]', buyBox);
      if (atcRecurEl) {
        if (state.mode === 'subscribe' && recSubCActive > 0 && recSubCActive !== firstSubC) {
          const freq = state.frequency;
          const cadence = freq === '30' ? '/ mo' : freq === '60' ? '/ 2 mo' : freq === '90' ? '/ 3 mo' : '/ refill';
          atcRecurEl.textContent = 'then ' + formatMoney(recSubCActive) + ' ' + cadence;
        } else {
          atcRecurEl.textContent = '';
        }
      }

      const headline = $('[data-buy-headline-price]', buyBox);
      if (headline) headline.textContent = state.mode === 'subscribe' ? formatMoney(firstSubC) : formatMoney(oneTimeC);

      // Inline price on the Add to Cart button — reflects the active selection
      const atcPriceEl = $('[data-atc-price]', buyBox);
      if (atcPriceEl) atcPriceEl.textContent = state.mode === 'subscribe' ? formatMoney(firstSubC) : formatMoney(oneTimeC);

      // Sticky cart: only the main (hero) buy box drives the sticky bar
      if (isMain) {
        const stickyImg = $('[data-sticky-image]');
        const stickyLabel = $('[data-sticky-label]');
        const stickyPrice = $('[data-sticky-price]');
        if (active) {
          const packName = active.dataset.name || '';
          const packImg = active.dataset.image || '';
          if (stickyImg && packImg) stickyImg.src = packImg;
          if (stickyLabel && packName) stickyLabel.textContent = 'PCQ Pet · ' + packName;
        }
        if (stickyPrice) stickyPrice.textContent = state.mode === 'subscribe' ? formatMoney(firstSubC) + ' first order' : formatMoney(oneTimeC);
      }
    };

    const resolveSellingPlanId = () => {
      const variant = findVariant(state.variantId);
      if (!variant || !variant.selling_plan_allocations || !variant.selling_plan_allocations.length) {
        state.sellingPlanId = null;
        return;
      }
      const want = parseInt(state.frequency, 10);
      const match = variant.selling_plan_allocations.find((a) => {
        const sp = a.selling_plan;
        if (!sp || !sp.name) return false;
        return sp.name.indexOf(String(want)) !== -1;
      });
      state.sellingPlanId = (match && match.selling_plan && match.selling_plan.id) || (variant.selling_plan_allocations[0] && variant.selling_plan_allocations[0].selling_plan && variant.selling_plan_allocations[0].selling_plan.id) || null;
    };

    // Pack click
    packCards.forEach((card) => {
      card.addEventListener('click', () => {
        state.packId = card.dataset.packId;
        updatePackUi();
        resolveSellingPlanId();
        recomputePricing();
      });
    });

    // Purchase mode click
    $$('.purchase-toggle__option', buyBox).forEach((opt) => {
      opt.addEventListener('click', () => {
        state.mode = opt.dataset.mode;
        updateModeUi();
        resolveSellingPlanId();
        recomputePricing();
      });
    });

    // Frequency click
    $$('.purchase-toggle__freq-pill', buyBox).forEach((pill) => {
      pill.addEventListener('click', () => {
        state.frequency = pill.dataset.frequency;
        updateFrequencyUi();
        resolveSellingPlanId();
        recomputePricing();
      });
    });

    // Form submit — inject correct id + selling_plan
    const form = $('[data-buy-form]', buyBox);
    if (form) {
      form.addEventListener('submit', () => {
        const idInput = $('input[name="id"]', form);
        const spInput = $('input[name="selling_plan"]', form);
        if (idInput) idInput.value = state.variantId;
        if (state.mode === 'subscribe' && state.sellingPlanId) {
          if (spInput) spInput.value = state.sellingPlanId;
        } else {
          if (spInput) spInput.value = '';
        }
      });
    }

    updatePackUi();
    updateModeUi();
    updateFrequencyUi();
    resolveSellingPlanId();
    recomputePricing();
    };
    buyBoxes.forEach(initBuyBox);

    // ---------- ATC popup (recommended Double Pack subscription) — uses main buy box ----------
    const mainBuyBox = buyBoxes[0];
    const mainPackCards = $$('.pack-card', mainBuyBox);
    const mainSubFirstPct = parseInt(mainBuyBox.dataset.subFirst || '20', 10);
    const atcPopup = document.querySelector('[data-modal="atc-confirm"]');
    if (atcPopup) {
      // Determine the recommended pack: most-popular if present, else second card, else first
      const recPack = mainPackCards.find((c) => c.dataset.mostPopular === 'true') || mainPackCards[1] || mainPackCards[0];
      const popupState = { frequency: '30', sellingPlanId: null };

      const populatePopup = () => {
        if (!recPack) return;
        const oneTimeC = parseInt(recPack.dataset.price || '0', 10);
        const firstSubC = Math.round(oneTimeC * (1 - mainSubFirstPct / 100));
        const recName = recPack.dataset.name || 'Double Pack';
        const recImg = recPack.dataset.image || '';
        // Anchor the strikethrough against the singles-multiplied "original" price (consistent with the buy box)
        const recBottles = parseInt(recPack.dataset.bottles || '1', 10);
        const singleBottleC = parseInt(mainBuyBox.dataset.singleBottleCents || '3987', 10);
        const originalC = singleBottleC * recBottles;
        const thumbEl = atcPopup.querySelector('[data-atc-popup-thumb]');
        const priceEl = atcPopup.querySelector('[data-atc-popup-price]');
        const compareEl = atcPopup.querySelector('[data-atc-popup-compare]');
        const variantInput = atcPopup.querySelector('[data-atc-popup-variant]');
        // Thumb: only auto-fill if the editor didn't already render an <img>
        if (thumbEl && !thumbEl.querySelector('img') && recImg) {
          thumbEl.innerHTML = '<img src="' + recImg + '" alt="' + recName + '">';
        }
        // Price: respect schema override (data-override). If empty, compute.
        if (priceEl && !priceEl.dataset.override) priceEl.textContent = formatMoney(firstSubC);
        if (compareEl && !compareEl.dataset.override) compareEl.textContent = formatMoney(originalC);
        if (variantInput) variantInput.value = recPack.dataset.variantId || '';
      };

      const resolvePopupSellingPlan = () => {
        const variant = findVariant(recPack && recPack.dataset.variantId);
        if (!variant || !variant.selling_plan_allocations || !variant.selling_plan_allocations.length) {
          popupState.sellingPlanId = null;
          return;
        }
        const want = parseInt(popupState.frequency, 10);
        const match = variant.selling_plan_allocations.find((a) => {
          const sp = a.selling_plan;
          if (!sp || !sp.name) return false;
          return sp.name.indexOf(String(want)) !== -1;
        });
        popupState.sellingPlanId = (match && match.selling_plan && match.selling_plan.id) || (variant.selling_plan_allocations[0] && variant.selling_plan_allocations[0].selling_plan && variant.selling_plan_allocations[0].selling_plan.id) || null;
        const planInput = atcPopup.querySelector('[data-atc-popup-plan]');
        if (planInput) planInput.value = popupState.sellingPlanId || '';
      };

      $$('.atc-popup__freq-pill', atcPopup).forEach((pill) => {
        pill.addEventListener('click', () => {
          popupState.frequency = pill.dataset.atcPopupFreq;
          $$('.atc-popup__freq-pill', atcPopup).forEach((p) => {
            p.dataset.active = (p.dataset.atcPopupFreq === popupState.frequency).toString();
          });
          resolvePopupSellingPlan();
        });
      });

      $$('[data-atc-popup-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          populatePopup();
          resolvePopupSellingPlan();
          atcPopup.dataset.open = 'true';
          atcPopup.setAttribute('aria-hidden', 'false');
          document.body.classList.add('has-drawer-open');
          openDialog(atcPopup);
        });
      });
    }
  }

  // ---------- 5. Sticky ATC visibility ----------
  const stickyAtc = $('.sticky-atc');
  const buyBoxEl = $('[data-buy-box]');
  if (stickyAtc && buyBoxEl && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // visible only when buy-box is OUT of view
        stickyAtc.dataset.visible = (!entry.isIntersecting).toString();
      });
    }, { threshold: 0, rootMargin: '0px 0px -100px 0px' });
    io.observe(buyBoxEl);
  }

  // ---------- 6. PDP timeline ----------
  // Static display: every week is shown in its full active state via
  // `data-active="true"` hard-coded in the liquid template. No scroll-driven
  // reveal, no hover swap, no frame switching.

  // ---------- 6b. Stat count-up on scroll-in ----------
  if ('IntersectionObserver' in window) {
    const stats = $$('[data-countup]');
    if (stats.length) {
      const parseStat = (raw) => {
        const match = (raw || '').match(/^(\D*?)(\d+(?:\.\d+)?)(.*)$/);
        if (!match) return null;
        return {
          prefix: match[1],
          target: parseFloat(match[2]),
          suffix: match[3],
          isInt: !match[2].includes('.'),
        };
      };

      // Reset every stat to its zero state immediately so users never see the final
      // number before the count-up animation starts.
      stats.forEach((el) => {
        const parsed = parseStat(el.dataset.countup || el.textContent || '');
        if (!parsed) return;
        if (prefersReducedMotion()) {
          el.textContent = parsed.prefix + (parsed.isInt ? parsed.target : parsed.target.toFixed(1)) + parsed.suffix;
          return;
        }
        el.textContent = parsed.prefix + (parsed.isInt ? '0' : '0.0') + parsed.suffix;
        el.style.fontVariantNumeric = 'tabular-nums';
      });

      const animateStat = (el) => {
        if (prefersReducedMotion()) return;
        const parsed = parseStat(el.dataset.countup || '');
        if (!parsed) return;
        const { prefix, target, suffix, isInt } = parsed;
        const duration = 1400;
        const startTime = performance.now();
        const ease = (t) => 1 - Math.pow(1 - t, 3);
        const tick = (now) => {
          const t = Math.min(1, (now - startTime) / duration);
          const current = target * ease(t);
          el.textContent = prefix + (isInt ? Math.round(current) : current.toFixed(1)) + suffix;
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      };
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateStat(entry.target);
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.4 });
      stats.forEach((el) => io.observe(el));
    }
  }

  // ---------- 6d. Review modal (See more) ----------
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-review-trigger]');
    if (!trigger) return;
    e.preventDefault();
    const card = trigger.closest('.pdp-reviews__card');
    const modal = document.querySelector('[data-modal="review"]');
    if (!card || !modal) return;
    const title = card.dataset.reviewTitle || '';
    const body = card.dataset.reviewBody || '';
    const author = card.dataset.reviewAuthor || '';
    const verified = card.dataset.reviewVerified === 'true';
    const titleEl = modal.querySelector('[data-review-modal-title]');
    const bodyEl = modal.querySelector('[data-review-modal-body]');
    const authorEl = modal.querySelector('[data-review-modal-author]');
    const verifiedBadge = modal.querySelector('[data-review-modal-verified-badge]');
    if (titleEl) titleEl.textContent = title;
    if (bodyEl) bodyEl.textContent = body;
    if (authorEl) authorEl.textContent = author;
    if (verifiedBadge) verifiedBadge.hidden = !verified;
    modal.dataset.open = 'true';
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('has-drawer-open');
    openDialog(modal);
  });
  document.addEventListener('click', (e) => {
    const closer = e.target.closest('[data-modal-close]');
    if (!closer) return;
    const modal = closer.closest('[data-modal]');
    if (modal) {
      modal.dataset.open = 'false';
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('has-drawer-open');
      closeDialog(modal);
    }
  });

  // ---------- 6c. Drawer (clinical evidence) ----------
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-trigger]');
    if (trigger) {
      e.preventDefault();
      const name = trigger.getAttribute('data-drawer-trigger');
      const drawer = document.querySelector('[data-drawer="' + name + '"]');
      if (drawer) {
        drawer.dataset.open = 'true';
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('has-drawer-open');
        openDialog(drawer);
      }
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      const drawer = closer.closest('[data-drawer]');
      if (drawer) {
        drawer.dataset.open = 'false';
        drawer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('has-drawer-open');
        closeDialog(drawer);
      }
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      $$('[data-drawer][data-open="true"], [data-modal][data-open="true"]').forEach((el) => {
        el.dataset.open = 'false';
        el.setAttribute('aria-hidden', 'true');
        closeDialog(el);
      });
      document.body.classList.remove('has-drawer-open');
    }
  });

  // Generic [data-modal-trigger="<name>"] opens [data-modal="<name>"]
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-modal-trigger]');
    if (!trigger) return;
    e.preventDefault();
    const name = trigger.getAttribute('data-modal-trigger');
    const modal = document.querySelector('[data-modal="' + name + '"]');
    if (!modal) return;
    modal.dataset.open = 'true';
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('has-drawer-open');
    openDialog(modal);
  });

  // ---------- Mobile carousel indicators: sync active dot with scroll position ----------
  const wireCarouselIndicator = (trackSel, itemSel, dotSel, activeClass) => {
    const track = $(trackSel);
    const items = $$(itemSel);
    const dots = $$(dotSel);
    if (!track || !items.length || items.length !== dots.length) return;
    let raf = null;
    const update = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let activeIdx = 0;
      let bestDist = Infinity;
      items.forEach((item, i) => {
        const itemCenter = item.offsetLeft + item.offsetWidth / 2;
        const dist = Math.abs(itemCenter - center);
        if (dist < bestDist) { bestDist = dist; activeIdx = i; }
      });
      dots.forEach((dot, i) => dot.classList.toggle(activeClass, i === activeIdx));
    };
    track.addEventListener('scroll', () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    }, { passive: true });
    update();
  };
  wireCarouselIndicator('.pdp-pillars__grid', '.pdp-pillars__col', '.pdp-pillars__dot', 'pdp-pillars__dot--active');
  wireCarouselIndicator('.pdp-formula__circles', '.pdp-formula__circle', '.pdp-formula__dot', 'pdp-formula__dot--active');

  // ---------- 7. Reveal-on-scroll ----------
  if ('IntersectionObserver' in window) {
    const revealItems = $$('[data-reveal]');
    if (revealItems.length) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.dataset.revealed = 'true';
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.15 });
      revealItems.forEach((el) => io.observe(el));
    }
  }
})();

/* === Cart page: AJAX stepper + remove === */
(function () {
  const cartRoot = document.querySelector('[data-cart-root]');
  if (!cartRoot) return;

  function fmtMoney(cents) {
    const n = (cents / 100).toFixed(2);
    return '$' + n;
  }

  function setCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      if (count > 0) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
  }

  function renderEmpty() {
    cartRoot.innerHTML = '<div data-cart-empty><p style="font-size:var(--fs-body-lg);color:var(--vps-bark);">Your cart is empty.</p><a href="/products/pcq-pet" class="btn btn--primary btn--lg" style="margin-top:24px;">Shop PCQ Pet</a></div>';
  }

  async function changeLine(line, quantity) {
    const key = line.dataset.lineKey;
    line.dataset.loading = 'true';
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      if (!res.ok) throw new Error('Cart update failed');
      const cart = await res.json();
      updateUI(cart, line, quantity);
    } catch (err) {
      console.error('[cart] change error', err);
      line.removeAttribute('data-loading');
    }
  }

  function updateUI(cart, line, newQty) {
    setCartCount(cart.item_count);

    if (cart.item_count === 0) {
      renderEmpty();
      return;
    }

    if (newQty === 0) {
      line.remove();
    } else {
      line.removeAttribute('data-loading');
      const matched = cart.items.find((it) => it.key === line.dataset.lineKey);
      if (matched) {
        const priceEl = line.querySelector('[data-line-price]');
        if (priceEl) priceEl.textContent = fmtMoney(matched.line_price);
        const input = line.querySelector('[data-qty-input]');
        if (input) input.value = matched.quantity;
      }
    }

    const totalEl = cartRoot.querySelector('[data-cart-total]');
    if (totalEl) totalEl.textContent = fmtMoney(cart.total_price);
  }

  cartRoot.addEventListener('click', (e) => {
    const line = e.target.closest('[data-cart-line]');
    if (!line) return;
    const input = line.querySelector('[data-qty-input]');
    if (!input) return;
    const current = parseInt(input.value, 10) || 0;

    if (e.target.closest('[data-qty-increment]')) {
      e.preventDefault();
      changeLine(line, current + 1);
    } else if (e.target.closest('[data-qty-decrement]')) {
      e.preventDefault();
      const next = Math.max(0, current - 1);
      changeLine(line, next);
    } else if (e.target.closest('[data-cart-remove]')) {
      e.preventDefault();
      changeLine(line, 0);
    }
  });

  let inputTimer;
  cartRoot.addEventListener('change', (e) => {
    if (!e.target.matches('[data-qty-input]')) return;
    const line = e.target.closest('[data-cart-line]');
    if (!line) return;
    const next = Math.max(0, parseInt(e.target.value, 10) || 0);
    clearTimeout(inputTimer);
    inputTimer = setTimeout(() => changeLine(line, next), 200);
  });
})();

/* === Cart drawer: open on ATC, render items, stepper + remove === */
(function () {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  const itemsEl = drawer.querySelector('[data-cart-drawer-items]');
  const emptyEl = drawer.querySelector('[data-cart-drawer-empty]');
  const footEl = drawer.querySelector('[data-cart-drawer-foot]');
  const totalEl = drawer.querySelector('[data-cart-drawer-total]');
  const shipEl = drawer.querySelector('[data-cart-ship]');
  const shipMsg = drawer.querySelector('[data-cart-ship-msg]');
  const shipFill = drawer.querySelector('[data-cart-ship-fill]');
  const shipThreshold = shipEl ? parseInt(shipEl.dataset.threshold || '0', 10) : 0;

  function fmtMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function setHeaderCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      if (count > 0) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    });
  }

  function lineHTML(item) {
    const img = item.image ? '<img class="cart-drawer-line__img" src="' + item.image.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)\./, '_200x.') + '" alt="" loading="lazy">' : '<div class="cart-drawer-line__img"></div>';
    const variant = item.variant_title && item.variant_title !== 'Default Title' ? '<div class="cart-drawer-line__variant">' + escapeHTML(item.variant_title) + '</div>' : '';
    const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan ? '<div class="cart-drawer-line__plan">' + escapeHTML(item.selling_plan_allocation.selling_plan.name) + '</div>' : '';
    return '<div class="cart-drawer-line" data-drawer-line data-line-key="' + escapeAttr(item.key) + '">' +
      img +
      '<div class="cart-drawer-line__info">' +
      '<a href="' + escapeAttr(item.url) + '" class="cart-drawer-line__title">' + escapeHTML(item.product_title) + '</a>' +
      variant + plan +
      '<div class="cart-drawer-line__row">' +
      '<div class="qty-stepper" role="group" aria-label="Quantity">' +
      '<button type="button" class="qty-stepper__btn" data-qty-decrement aria-label="Decrease quantity">&minus;</button>' +
      '<input type="number" class="qty-stepper__input" value="' + item.quantity + '" min="0" inputmode="numeric" aria-label="Quantity" data-qty-input>' +
      '<button type="button" class="qty-stepper__btn" data-qty-increment aria-label="Increase quantity">+</button>' +
      '</div>' +
      '<button type="button" class="cart-drawer-line__remove" data-drawer-remove aria-label="Remove">Remove</button>' +
      '</div>' +
      '</div>' +
      '<div class="cart-drawer-line__price" data-line-price>' + fmtMoney(item.line_price) + '</div>' +
      '</div>';
  }

  function escapeHTML(str) {
    return String(str || '').replace(/[&<>"']/g, (m) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[m]));
  }
  function escapeAttr(str) {
    return escapeHTML(str);
  }

  function renderCart(cart) {
    setHeaderCount(cart.item_count);
    if (cart.item_count === 0) {
      itemsEl.hidden = true;
      itemsEl.innerHTML = '';
      emptyEl.hidden = false;
      footEl.hidden = true;
      if (shipEl) shipEl.hidden = true;
      return;
    }
    emptyEl.hidden = true;
    itemsEl.hidden = false;
    footEl.hidden = false;
    itemsEl.innerHTML = cart.items.map(lineHTML).join('');
    totalEl.textContent = fmtMoney(cart.total_price);
    updateShipBar(cart.total_price);
  }

  function updateShipBar(totalCents) {
    if (!shipEl || shipThreshold <= 0) return;
    shipEl.hidden = false;
    if (totalCents >= shipThreshold) {
      shipMsg.innerHTML = '<strong>You unlocked free shipping.</strong>';
      shipFill.style.width = '100%';
    } else {
      const remaining = shipThreshold - totalCents;
      shipMsg.innerHTML = 'You\'re <strong>' + fmtMoney(remaining) + '</strong> away from free shipping.';
      shipFill.style.width = Math.max(2, Math.round((totalCents / shipThreshold) * 100)) + '%';
    }
  }

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      return await res.json();
    } catch (e) {
      console.error('[drawer] fetch cart', e);
      return null;
    }
  }

  async function changeKey(key, quantity, lineEl) {
    if (lineEl) lineEl.dataset.loading = 'true';
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      const cart = await res.json();
      renderCart(cart);
    } catch (e) {
      console.error('[drawer] change', e);
      if (lineEl) lineEl.removeAttribute('data-loading');
    }
  }

  function openDrawer() {
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
  }
  function closeDrawer() {
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
  }

  // close handlers
  drawer.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-drawer-close]')) {
      e.preventDefault();
      closeDrawer();
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') closeDrawer();
  });

  // stepper + remove inside drawer
  drawer.addEventListener('click', (e) => {
    const line = e.target.closest('[data-drawer-line]');
    if (!line) return;
    const input = line.querySelector('[data-qty-input]');
    const current = input ? parseInt(input.value, 10) || 0 : 0;
    if (e.target.closest('[data-qty-increment]')) {
      e.preventDefault();
      changeKey(line.dataset.lineKey, current + 1, line);
    } else if (e.target.closest('[data-qty-decrement]')) {
      e.preventDefault();
      changeKey(line.dataset.lineKey, Math.max(0, current - 1), line);
    } else if (e.target.closest('[data-drawer-remove]')) {
      e.preventDefault();
      changeKey(line.dataset.lineKey, 0, line);
    }
  });

  let drawerInputTimer;
  drawer.addEventListener('change', (e) => {
    if (!e.target.matches('[data-qty-input]')) return;
    const line = e.target.closest('[data-drawer-line]');
    if (!line) return;
    const next = Math.max(0, parseInt(e.target.value, 10) || 0);
    clearTimeout(drawerInputTimer);
    drawerInputTimer = setTimeout(() => changeKey(line.dataset.lineKey, next, line), 200);
  });

  // Header cart icon → open drawer (instead of nav). Exclude the Account link
  // which shares .header__cart for styling but must navigate to /account.
  document.querySelectorAll('a.header__cart:not(.header__account)').forEach((a) => {
    a.addEventListener('click', async (e) => {
      e.preventDefault();
      const cart = await fetchCart();
      if (cart) renderCart(cart);
      openDrawer();
    });
  });

  // Intercept ATC forms: post add.js, then refresh + open drawer.
  // Bubble phase (no capture) so per-form submit listeners (e.g. buy-box state writer)
  // run FIRST and update hidden id/selling_plan before we serialize FormData.
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = (form.getAttribute('action') || '').split('?')[0];
    if (!/\/cart\/add$/.test(action)) return;
    // Skip if it's the bare cart page form (updates[]/checkout); only intercept ATC
    if (form.querySelector('[name="update"], [name="checkout"]')) return;

    e.preventDefault();
    const submitter = e.submitter;
    const originalLabel = submitter ? submitter.innerHTML : null;
    if (submitter) {
      submitter.disabled = true;
      submitter.classList.add('is-loading');
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('[drawer] add error', err);
        throw new Error(err.description || 'Add to cart failed');
      }
      const cart = await fetchCart();
      if (cart) renderCart(cart);
      openDrawer();
    } catch (err) {
      console.error('[drawer] submit', err);
      // fallback: let the form submit normally
      form.submit();
    } finally {
      if (submitter) {
        submitter.disabled = false;
        submitter.classList.remove('is-loading');
        if (originalLabel) submitter.innerHTML = originalLabel;
      }
    }
  });
})();
